import React from "react";
import { AlertTriangle, X, Camera, Loader2, MapPin, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { driverApi } from "../lib/api";

const CATEGORIES = [
  { key: "car_broken", label: "Bilen er gået i stykker", severity: "high", icon: "🚗" },
  { key: "accident", label: "Trafikuheld", severity: "high", icon: "⚠️" },
  { key: "recipient_not_home", label: "Modtager ikke hjemme", severity: "low" },
  { key: "wrong_address", label: "Forkert adresse", severity: "medium" },
  { key: "refused_receipt", label: "Modtager nægter modtagelse", severity: "medium" },
  { key: "damaged_package", label: "Pakken er beskadiget", severity: "medium" },
  { key: "other", label: "Andet", severity: "medium" },
];

const SEVERITY_LABEL = { low: "Lav", medium: "Mellem", high: "Høj — drift kontaktes straks" };

const compressImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const maxDim = 1280;
      let w = img.width, h = img.height;
      if (w > h && w > maxDim) { h = Math.round(h * maxDim / w); w = maxDim; }
      else if (h > maxDim) { w = Math.round(w * maxDim / h); h = maxDim; }
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", 0.72));
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function DriverProblemReportModal({ booking, onClose, onSubmitted }) {
  const [category, setCategory] = React.useState(CATEGORIES[0].key);
  const [severity, setSeverity] = React.useState("high");
  const [note, setNote] = React.useState("");
  const [photo, setPhoto] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [uploadingFile, setUploadingFile] = React.useState(false);
  const [coords, setCoords] = React.useState(null);
  const fileRef = React.useRef(null);

  React.useEffect(() => {
    // Auto-fetch GPS on open
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, timeout: 5000 },
    );
  }, []);

  React.useEffect(() => {
    // Auto-bump severity when selecting car_broken / accident
    const c = CATEGORIES.find((c) => c.key === category);
    if (c?.severity === "high") setSeverity("high");
  }, [category]);

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingFile(true);
    try {
      const data = await compressImage(file);
      setPhoto(data);
    } catch {
      toast.error("Kunne ikke læse billedet");
    } finally { setUploadingFile(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        category,
        severity,
        note: note || undefined,
        photo_base64: photo || undefined,
        lat: coords?.lat,
        lng: coords?.lng,
        booking_id: booking?.id,
      };
      const { data } = await driverApi().post("/driver/problem-report", payload);
      if (data.auto_paused) {
        toast.warning("Rapport sendt — du er auto-pauset fra nye opgaver. Drift kontakter dig.", { duration: 8000 });
      } else {
        toast.success("Rapport sendt — drift gennemgår den");
      }
      onSubmitted?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke sende rapport");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-5" data-testid="problem-report-modal">
      <div className="bg-white w-full sm:max-w-md max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-brand p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle size={20} className="text-brand-red" />
            <h2 className="font-display font-bold text-lg text-brand-dark">Rapportér problem</h2>
          </div>
          <button onClick={onClose} className="text-brand-text2 hover:text-brand-dark" data-testid="problem-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {booking?.booking_number && (
            <div className="text-xs text-brand-text2">
              For booking <span className="font-mono font-bold text-brand-dark">{booking.booking_number}</span>
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-widest text-brand-text2 font-bold mb-2 block">Kategori</label>
            <div className="grid grid-cols-1 gap-1.5">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setCategory(c.key)}
                  data-testid={`problem-cat-${c.key}`}
                  className={`text-left p-3 border text-sm flex items-center justify-between transition-colors ${
                    category === c.key
                      ? "border-brand-red bg-red-50 text-brand-dark font-bold"
                      : "border-brand text-brand-dark hover:border-brand-dark"
                  }`}
                >
                  <span>{c.label}</span>
                  {c.severity === "high" && (
                    <span className="text-[10px] uppercase bg-red-700 text-white px-2 py-0.5 font-bold">AKUT</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-brand-text2 font-bold mb-2 block">Alvorlighed</label>
            <div className="grid grid-cols-3 gap-1.5">
              {["low", "medium", "high"].map((s) => (
                <button
                  key={s}
                  onClick={() => setSeverity(s)}
                  data-testid={`problem-sev-${s}`}
                  className={`p-2 text-xs uppercase font-bold border transition-colors ${
                    severity === s
                      ? s === "high" ? "border-red-700 bg-red-700 text-white" : "border-brand-dark bg-brand-dark text-white"
                      : "border-brand text-brand-dark hover:border-brand-dark"
                  }`}
                >
                  {SEVERITY_LABEL[s].split(" — ")[0]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-brand-text2 font-bold mb-2 block">Beskrivelse</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Fortæl kort hvad der skete..."
              className="w-full border border-brand p-2 text-sm focus:outline-none focus:border-brand-dark"
              data-testid="problem-note"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-brand-text2 font-bold mb-2 block">Foto (valgfrit)</label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onPickPhoto}
              className="hidden"
              data-testid="problem-file"
            />
            {photo ? (
              <div className="relative">
                <img src={photo} alt="Foto" className="w-full max-h-48 object-cover border border-brand" />
                <button
                  onClick={() => setPhoto(null)}
                  className="absolute top-2 right-2 bg-white border border-brand p-2 hover:bg-red-50"
                  data-testid="problem-photo-remove"
                >
                  <Trash2 size={14} className="text-red-700" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploadingFile}
                className="w-full border-2 border-dashed border-brand p-4 text-sm text-brand-text2 hover:border-brand-dark hover:text-brand-dark flex items-center justify-center gap-2"
                data-testid="problem-add-photo"
              >
                {uploadingFile ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                Tilføj foto
              </button>
            )}
          </div>

          <div className="text-xs text-brand-text2 flex items-center gap-1">
            <MapPin size={11} />
            {coords ? (
              <>GPS: {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}</>
            ) : (
              <>Henter GPS…</>
            )}
          </div>

          {severity === "high" && (
            <div className="bg-red-50 border border-red-300 p-3 text-xs text-red-900">
              <b>Vigtigt:</b> Du vil blive auto-pauset fra nye opgaver. Drift kontaktes straks med SMS + email.
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-brand p-4 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 border border-brand text-brand-dark px-4 py-3 hover:bg-brand-bg text-sm font-bold"
            data-testid="problem-cancel"
          >
            Annullér
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="flex-1 bg-brand-red text-white px-4 py-3 hover:bg-red-700 disabled:opacity-60 text-sm font-bold flex items-center justify-center gap-2"
            data-testid="problem-submit"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
            Send rapport
          </button>
        </div>
      </div>
    </div>
  );
}
